<template>
  <div class="cp">
    <!-- compact strip — opens the contact dialog -->
    <button type="button" class="cp-strip" @click="dialog = true">
      <span class="cp-strip__icon"><hero-icon :icon="UserIcon" :size="18" /></span>
      <span class="cp-strip__text">
        <span class="cp-strip__name">{{ inquiry.name || 'Contacto sin nombre' }}</span>
        <span class="cp-strip__sub">{{ sub }}</span>
      </span>
      <hero-icon :icon="ArrowIcon" :size="15" class="cp-strip__go" />
    </button>

    <!-- contact dialog -->
    <v-dialog v-model="dialog" max-width="520" :fullscreen="$vuetify.breakpoint.smAndDown" content-class="cp-dialog">
      <div class="cp-card">
        <div class="cp-card__head">
          <div class="cp-card__id">
            <div class="cp-card__eyebrow">Contacto</div>
            <h2 class="cp-card__name">{{ inquiry.name || 'Contacto sin nombre' }}</h2>
          </div>
          <button
            type="button"
            class="cp-card__close"
            @click="dialog = false"
            aria-label="Cerrar"
          >
            <hero-icon :icon="XMarkIcon" :size="18" />
          </button>
        </div>

        <div class="cp-card__divider"></div>

        <div class="cp-card__body">
          <!-- channels -->
          <div class="cp-rows">
            <a v-if="inquiry.email" class="cp-row cp-row--link" :href="'mailto:' + inquiry.email">
              <hero-icon :icon="EnvelopeIcon" :size="15" /><span>{{ inquiry.email }}</span>
            </a>
            <a v-if="inquiry.phone" class="cp-row cp-row--link" :href="'tel:' + inquiry.phone">
              <hero-icon :icon="PhoneIcon" :size="15" /><span>{{ inquiry.phone }}</span>
            </a>
            <div v-if="inquiry.location" class="cp-row">
              <hero-icon :icon="MapPinIcon" :size="15" /><span>{{ cap(inquiry.location) }}</span>
            </div>
            <div v-if="!inquiry.email && !inquiry.phone && !inquiry.location" class="cp-row cp-row--muted">
              Sin datos de contacto adicionales.
            </div>
          </div>

          <!-- inferred search intent -->
          <template v-if="hasIntent">
            <div class="cp-label">Lo que busca</div>
            <div v-if="intentTags.length" class="cp-tags">
              <span v-for="t in intentTags" :key="t" class="cp-tag">{{ t }}</span>
            </div>
            <div v-if="characteristics.length" class="cp-specs">
              <div v-for="c in characteristics" :key="c.key" class="cp-spec">
                <span class="cp-spec__k">{{ cap(c.key) }}</span>
                <span class="cp-spec__v">{{ c.value }}</span>
              </div>
            </div>
          </template>
        </div>

        <div class="cp-card__actions">
          <slot name="actions"></slot>
          <v-btn text color="primary" class="rounded-lg" @click="dialog = false">
            Cerrar
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import HeroIcon from "@/components/shared/HeroIcon";
import UserIcon from "heroicons/24/outline/user.svg";
import EnvelopeIcon from "heroicons/24/outline/envelope.svg";
import PhoneIcon from "heroicons/24/outline/phone.svg";
import MapPinIcon from "heroicons/24/outline/map-pin.svg";
import ArrowIcon from "heroicons/24/outline/arrow-up-right.svg";
import XMarkIcon from "heroicons/24/outline/x-mark.svg";

export default {
  name: "ContactPreview",

  components: { HeroIcon },

  props: {
    inquiry: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    dialog: false,
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    ArrowIcon,
    XMarkIcon
  }),

  computed: {
    sub() {
      const bits = [this.inquiry.email, this.inquiry.phone].filter(Boolean);
      return bits.length ? bits.join(" · ") : "Ver datos del contacto";
    },

    intentTags() {
      const tags = [];
      const OP = { sell: "Venta", rent: "Alquiler" };
      if (this.inquiry.operationType)
        tags.push(OP[this.inquiry.operationType] || this.cap(this.inquiry.operationType));
      if (this.inquiry.investmentType)
        tags.push(this.$t("investmentTypes." + this.inquiry.investmentType));
      if (this.inquiry.location) tags.push(this.cap(this.inquiry.location));
      return tags;
    },

    characteristics() {
      const c = this.inquiry.characteristics || {};
      return Object.keys(c).map(k => ({ key: k, value: c[k] }));
    },

    hasIntent() {
      return this.intentTags.length > 0 || this.characteristics.length > 0;
    }
  },

  methods: {
    cap(s) {
      return s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s;
    }
  }
};
</script>

<style scoped>
/* compact strip (mirrors the property strip) */
.cp-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: #faf8f4;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.cp-strip:hover {
  border-color: #dcd4c6;
  background: #f5f1ea;
}
.cp-strip__icon {
  flex: none;
  width: 42px;
  height: 42px;
  border-radius: 9px;
  background: #1a1814;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cp-strip__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.cp-strip__name {
  font-size: 13.5px;
  font-weight: 600;
  color: #1a1814;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cp-strip__sub {
  font-size: 12px;
  color: #756f64;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cp-strip__go { flex: none; color: #b52118; }

/* contact dialog */
.cp-card { background: #fff; border-radius: 12px; }
.cp-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 14px;
}
.cp-card__eyebrow {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
}
.cp-card__name { font-size: 19px; font-weight: 700; color: #1a1814; margin: 4px 0 0; line-height: 1.2; }
.cp-card__close {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: none;
  background: none;
  cursor: pointer;
  color: #a39a8c;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}
.cp-card__close:hover { background: #f1ece3; color: #1a1814; }
.cp-card__divider { height: 1px; background: #ece7de; }

.cp-card__body { padding: 16px 22px 6px; }
.cp-rows { display: flex; flex-direction: column; gap: 9px; }
.cp-row {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-size: 13.5px;
  color: #56504a;
  text-decoration: none;
  width: fit-content;
  max-width: 100%;
}
.cp-row span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cp-row .hero-icon { color: #a39a8c; flex: none; }
.cp-row--link { transition: color 0.15s ease; }
.cp-row--link:hover { color: #b52118; }
.cp-row--link:hover .hero-icon { color: #b52118; }
.cp-row--muted { color: #a39a8c; font-size: 13px; }

.cp-label {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
  margin: 18px 0 9px;
}
.cp-tags { display: flex; flex-wrap: wrap; gap: 7px; }
.cp-tag {
  font-size: 11.5px;
  font-weight: 600;
  color: #1a1814;
  background: #ece7de;
  border-radius: 999px;
  padding: 3px 11px;
}
.cp-specs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 18px;
  margin-top: 12px;
}
.cp-spec {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid #f1ece3;
  padding-bottom: 6px;
}
.cp-spec__k { font-size: 12.5px; color: #756f64; }
.cp-spec__v { font-size: 13px; font-weight: 600; color: #1a1814; }

.cp-card__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 22px 16px;
}
</style>
